/**
 * 計助ダッシュボード: 回帰・検量線（散布＋直線）
 */

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KeisukeChart from '../charts/KeisukeChart';
import KeisukeAxis from '../charts/KeisukeAxis';
import KeisukeScatter from '../charts/KeisukeScatter';
import { createLinearScale, calculateNiceTicks } from '../charts/chartUtils';
import type { AxisScale, ChartBounds, ChartDimensions, ScatterPoint } from '../charts/types';
import styles from './KeisukeDashboardChart.module.scss';
import { DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX } from './keisukeDashboardBarChartSettings';
import { KeisukeDashboardChartExportDropdown } from './KeisukeDashboardChartExportDropdown';
import { useKeisukeDashboardChartSvgExport } from './keisukeDashboardChartSvgExport';

const MARGIN = { top: 40, right: 40, bottom: 60, left: 80 };
const STANDARD_POINT_COLOR = '#1f77b4';
const UNKNOWN_POINT_COLOR = '#d62728';

export type KeisukeDashboardRegressionChartLayout = 'split-settings' | 'split-plot';

interface Props {
  observed: [number, number][];
  line: [number, number][];
  /** 検量線: 推定した未知検体 [濃度, 吸光度] */
  unknown?: [number, number][];
  xLabel?: string;
  yLabel?: string;
  isCalculating?: boolean;
  layout: KeisukeDashboardRegressionChartLayout;
}

export default function KeisukeDashboardRegressionChart({
  observed,
  line,
  unknown = [],
  xLabel = '',
  yLabel = '',
  isCalculating = false,
  layout,
}: Props) {
  const { t } = useTranslation();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const exportHandlers = useKeisukeDashboardChartSvgExport(chartContainerRef, 'keisuke_regression');

  const scatterData: ScatterPoint[] = useMemo(() => {
    const standard = observed.map(([x, y]) => ({ x, y, color: STANDARD_POINT_COLOR }));
    const extra = unknown.map(([x, y]) => ({ x, y, color: UNKNOWN_POINT_COLOR }));
    return [...standard, ...extra];
  }, [observed, unknown]);

  const xDomain = useMemo((): [number, number] => {
    const vals = [
      ...observed.map(([x]) => x),
      ...line.map(([x]) => x),
      ...unknown.map(([x]) => x),
    ];
    if (vals.length === 0) return [0, 1];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.05 || 1;
    return [min - pad, max + pad];
  }, [observed, line, unknown]);

  const yDomain = useMemo((): [number, number] => {
    const vals = [
      ...observed.map(([, y]) => y),
      ...line.map(([, y]) => y),
      ...unknown.map(([, y]) => y),
    ];
    if (vals.length === 0) return [0, 1];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.05 || 1;
    return [min - pad, max + pad];
  }, [observed, line, unknown]);

  const xTicks = useMemo(() => calculateNiceTicks(xDomain, 5), [xDomain]);
  const yTicks = useMemo(() => calculateNiceTicks(yDomain, 5), [yDomain]);

  const plotAreaBounds: ChartBounds = useMemo(
    () => ({
      x: MARGIN.left,
      y: MARGIN.top,
      width: 640 - MARGIN.left - MARGIN.right,
      height: DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX - MARGIN.top - MARGIN.bottom,
    }),
    []
  );

  const xScale = useMemo(
    () => createLinearScale(xDomain, [0, plotAreaBounds.width]),
    [xDomain, plotAreaBounds.width]
  );
  const yScale = useMemo(
    () => createLinearScale(yDomain, [0, plotAreaBounds.height]),
    [yDomain, plotAreaBounds.height]
  );
  const yScaleRev = (v: number) => plotAreaBounds.height - yScale(v);

  const linePath = useMemo(() => {
    if (line.length === 0) return '';
    return line
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${xScale(x)},${yScaleRev(y)}`)
      .join(' ');
  }, [line, xScale, yScaleRev]);

  if (layout === 'split-plot' && observed.length === 0) {
    return (
      <div className={`${styles.chartRootWrapper} ${styles.chartPlaceholder}`}>
        <span className="text-muted small">
          {t('keisuke.workspace.chartRunAnalysis')}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={chartContainerRef}
      className={`${styles.chartRootWrapper} ${isCalculating ? styles.chartRootCalculating : ''}`}
    >
      {layout === 'split-plot' && (
        <KeisukeDashboardChartExportDropdown
          onCopyClipboard={() => void exportHandlers.handleCopySvgToClipboard()}
          onExportPng={exportHandlers.handleExportPng}
          onExportSvg={exportHandlers.handleExportSvg}
          onExportTiff={exportHandlers.handleExportTiff}
          onExportJpeg={exportHandlers.handleExportJpeg}
        />
      )}
      <KeisukeChart width={layout === 'split-plot' ? undefined : 640} height={DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX}>
        {(dimensions: ChartDimensions) => {
          const xAxisScale: AxisScale = {
            domain: xDomain,
            range: [0, plotAreaBounds.width],
            ticks: xTicks,
          };
          const yAxisScale: AxisScale = {
            domain: yDomain,
            range: [0, plotAreaBounds.height],
            ticks: yTicks,
          };
          return (
            <>
              <KeisukeAxis
                bounds={plotAreaBounds}
                dimensions={dimensions}
                type="x"
                scale={xAxisScale}
                label={xLabel || t('keisuke.sampleData.columns.x')}
              />
              <KeisukeAxis
                bounds={plotAreaBounds}
                dimensions={dimensions}
                type="y"
                scale={yAxisScale}
                label={yLabel || t('keisuke.sampleData.columns.y')}
                showGrid
              />
              {/* 回帰直線はプロット原点へ平行移動した座標系で描画 */}
              <g transform={`translate(${plotAreaBounds.x}, ${plotAreaBounds.y})`} data-keisuke-chart-graph>
                {linePath && <path d={linePath} fill="none" stroke="#222" strokeWidth={2} />}
              </g>
              {/* KeisukeScatter は内部で bounds 分の translate を行うため、ここでは入れ子にしない
                  （二重 translate で観測点が右下にずれるのを防ぐ。用量反応グラフと同じ構造） */}
              <KeisukeScatter
                bounds={plotAreaBounds}
                data={scatterData}
                xScale={xScale}
                yScale={yScale}
                pointRadius={5}
              />
            </>
          );
        }}
      </KeisukeChart>
    </div>
  );
}
